import { useGlobalContext } from "@aleksundshantu.com/aunds-utils-react/contexts";
import styles from "./OptionGrid.module.scss";
import { useRef, useState } from "react";
import { ReactSVG } from "react-svg";
import { cleanTipTopfFilterLabel } from "@/utils/stringManipulation";
import { Input } from "@/components/Global/Input/Input";

type OptionGridProps = {
  title: string;
  options: Array<{ id: number; label: string }>;
  shouldCollapse: boolean;
  selectedValues: number[];
  onChange: (option: number) => void;
  addDifficultyIcons?: boolean;
};

export const OptionGrid = ({
  options,
  title,
  shouldCollapse,
  selectedValues,
  onChange,
  addDifficultyIcons = false,
}: OptionGridProps) => {
  const { getIcon } = useGlobalContext();
  const contentRef = useRef<HTMLDivElement>(null);

  const [isOpen, setIsOpen] = useState(false);
  const contentId = `collapsible-content-${title.toLowerCase()}`;

  const getCheckboxLabel = (
    option: string,
    amountLevels: number,
    levelIndex: number,
  ) => {
    const cleanedOptionLabel = cleanTipTopfFilterLabel(option);
    if (!addDifficultyIcons)
      return <p className="pl-4 required-text">{cleanedOptionLabel}</p>;

    return (
      <div className={`${styles["difficulty-label"]} pl-4 required-text`}>
        <p>{cleanedOptionLabel}</p>
        {Array.from({ length: amountLevels }).map((_, idx) => (
          <ReactSVG
            key={`level-catefory-${idx}`}
            src={getIcon("levelCategory")}
            className={`${idx <= levelIndex ? styles["fill-black"] : styles["fill-grey"]} ${idx === 0 && styles["first-svg"]}`}
            wrapper="svg"
            width={21}
            height={21}
            aria-hidden
          />
        ))}
      </div>
    );
  };

  if (!shouldCollapse) {
    return (
      <section>
        <p className={styles["filter-title"]}>{title}</p>
        <div className={styles["filter-option-grid"]}>
          {options.map((option, idx) => (
            <Input
              key={`checkbox-${option.label}-${option.id}}`}
              inputType="checkbox"
              name={`checkbox-${option.label}`}
              onChange={() => onChange(option.id)}
              id={`checkbox-${option.label}-${option.id}`}
              checked={selectedValues.includes(option.id)}
              label_checkbox={getCheckboxLabel(
                option.label,
                options.length,
                idx,
              )}
            />
          ))}
        </div>
      </section>
    );
  }

  // create a random id generator for below

  return (
    <div className={styles["collapsible-wrapper"]}>
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        aria-expanded={isOpen}
        aria-controls={contentId}
      >
        {title}
        <ReactSVG
          className={`${styles["icon"]} ${isOpen ? styles["icon-open"] : ""}`}
          src={getIcon("circlePlus")}
          width={21}
          height={21}
          wrapper="svg"
          aria-hidden
        />
      </button>

      <div
        id={contentId}
        ref={contentRef}
        aria-hidden={!isOpen}
        style={
          isOpen
            ? { height: `${contentRef?.current?.scrollHeight}px` }
            : { height: 0 }
        }
        className={`${styles["content"]} ${isOpen ? styles["show-content"] : styles["hide-content"]}`}
      >
        {options.map((option, idx) => (
          <Input
            key={`checkbox-${option.id}`}
            inputType="checkbox"
            name={`checkbox-${option.label}`}
            onChange={() => onChange(option.id)}
            tabIndex={isOpen ? 0 : -1}
            id={`checkbox-${option.label}-${option.id}`}
            checked={selectedValues.includes(option.id)}
            label_checkbox={getCheckboxLabel(option.label, options.length, idx)}
          />
        ))}
      </div>
    </div>
  );
};
